import React, { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';

export const PAGE_SIZE = 48;

// Order + swatch dot for each display-only color family; only families present in the catalog are shown.
export const COLOR_FAMILIES = [
  ['White', '#ffffff'], ['Ivory / Off-White', '#f4efe2'], ['Beige / Cream', '#e3d5bc'], ['Taupe', '#a39383'],
  ['Gray', '#9a9a9a'], ['Black', '#1f1f1f'], ['Brown', '#6b4a32'], ['Blue', '#3f5f8a'], ['Green', '#5f7a4d'],
  ['Red / Burgundy', '#7e2233'], ['Orange / Terracotta', '#c46a3b'], ['Yellow / Gold', '#d1a737'], ['Pink / Rose', '#e3a1a6'],
  ['Purple', '#6a4a7c'], ['Multi / Patterned', 'conic-gradient(#c46a3b,#3f5f8a,#5f7a4d,#d1a737,#c46a3b)'],
];
const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
// Spelling variants customers use (grey/gray, black-out/blackout)
export const normalize = (v) => (v || '').toLowerCase().replace(/grey/g, 'gray').replace(/black[\s-]?out/g, 'blackout').replace(/motoris/g, 'motoriz');

const matches = (c, f, skip) => (skip === 'brand' || !f.brand || c.brand === f.brand)
  && (skip === 'collection' || !f.collection || c.collection === f.collection)
  && (skip === 'type' || !f.type || c.fabric_type === f.type)
  && (!f.material || c.material === f.material)
  && (skip === 'color' || !f.color || (c.color_families || []).includes(f.color))
  && (!f.q || [c.name, c.code, c.collection, c.material, c.brand, c.fabric_type, ...(c.color_families || [])].some((v) => v && normalize(v).includes(f.q)));

// Array fields (color_families) count the fabric once under each value
const countBy = (list, key) => list.reduce((m, c) => { [c[key]].flat().filter(Boolean).forEach((v) => { m[v] = (m[v] || 0) + 1; }); return m; }, {});

// Search + Brand / Collection / Type / Color filters over the full fabric list; only `limit` items are rendered.
export const useSwatchFilter = (colors) => {
  const [state, setState] = useState({ query: '', brand: '', collection: '', type: '', color: '', material: '' });
  const [limit, setLimit] = useState(PAGE_SIZE);
  const f = { ...state, q: normalize(state.query.trim()) };

  const filtered = useMemo(() => colors.filter((c) => matches(c, f)), [colors, state]); // eslint-disable-line react-hooks/exhaustive-deps
  // Counts for each option given the other active filters
  const facets = useMemo(() => ({
    brand: countBy(colors.filter((c) => matches(c, f, 'brand')), 'brand'),
    collection: countBy(colors.filter((c) => matches(c, f, 'collection')), 'collection'),
    type: countBy(colors.filter((c) => matches(c, f, 'type')), 'fabric_type'),
    color: countBy(colors.filter((c) => matches(c, f, 'color')), 'color_families'),
  }), [colors, state]); // eslint-disable-line react-hooks/exhaustive-deps
  const all = useMemo(() => ({
    brands: [...new Set(colors.map((c) => c.brand).filter(Boolean))].sort(),
    collections: [...new Set(colors.map((c) => c.collection).filter(Boolean))].sort(),
    types: [...new Set(colors.map((c) => c.fabric_type).filter(Boolean))].sort(),
    colors: COLOR_FAMILIES.filter(([name]) => colors.some((c) => (c.color_families || []).includes(name))),
  }), [colors]);

  const set = (key) => (v) => { setState((s) => ({ ...s, [key]: v })); setLimit(PAGE_SIZE); };
  return {
    ...state, ...all, facets, filtered,
    visible: filtered.slice(0, limit),
    // Typing a new search drops the exact material set by a search link
    setQuery: (v) => { setState((s) => ({ ...s, query: v, material: '' })); setLimit(PAGE_SIZE); },
    setMaterial: set('material'), setBrand: set('brand'), setCollection: set('collection'), setType: set('type'), setColor: set('color'),
    clearAll: () => { setState({ query: '', brand: '', collection: '', type: '', color: '', material: '' }); setLimit(PAGE_SIZE); },
    loadMore: () => setLimit((l) => l + PAGE_SIZE),
  };
};

const Chip = ({ active, onClick, children, testid, disabled }) => (
  <button type="button" onClick={onClick} data-testid={testid} disabled={disabled} aria-pressed={active}
    className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors disabled:opacity-40 ${active ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
    {children}
  </button>
);

const Select = ({ value, onChange, all, counts, label, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}
    className="sm:w-52 py-2.5 px-3 rounded-lg border border-neutral-200 text-sm bg-white focus:outline-none focus:border-amber-500" data-testid={testid}>
    <option value="">{label}</option>
    {all.map((o) => <option key={o} value={o} disabled={!counts[o] && value !== o}>{o} ({counts[o] || 0})</option>)}
  </select>
);

const ColorChips = ({ f }) => (
  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:flex-wrap sm:overflow-visible" data-testid="swatch-color-chips">
    <Chip active={!f.color} onClick={() => f.setColor('')} testid="swatch-color-all">All colors</Chip>
    {f.colors.map(([name, dot]) => (
      <Chip key={name} active={f.color === name} onClick={() => f.setColor(f.color === name ? '' : name)} disabled={!f.facets.color[name] && f.color !== name} testid={`swatch-color-${slug(name)}`}>
        <span className="w-3 h-3 rounded-full border border-neutral-300" style={{ background: dot }} />
        {name} <span className="opacity-60">{f.facets.color[name] || 0}</span>
      </Chip>
    ))}
  </div>
);

const ActiveFilters = ({ f }) => {
  const active = [['Search', f.query, f.setQuery], ['Brand', f.brand, f.setBrand], ['Collection', f.collection, f.setCollection], ['Type', f.type, f.setType], ['Color', f.color, f.setColor]].filter(([, v]) => v);
  if (!active.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="swatch-active-filters">
      {active.map(([label, v, clear]) => (
        <button key={label} type="button" onClick={() => clear('')} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-amber-300 bg-amber-50 text-xs text-amber-900 hover:bg-amber-100" data-testid={`swatch-clear-${slug(label)}`}>
          {label}: {v} <X className="w-3 h-3" />
        </button>
      ))}
      <button type="button" onClick={f.clearAll} className="text-xs font-medium text-neutral-600 underline underline-offset-2 hover:text-neutral-900" data-testid="swatch-clear-all">Clear all</button>
    </div>
  );
};

export const SwatchFilterBar = ({ f, total }) => (
  <div className="mb-4 space-y-3" data-testid="swatch-filter-bar">
    <div className="flex flex-col sm:flex-row gap-2">
      <label className="relative flex-1">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input value={f.query} onChange={(e) => f.setQuery(e.target.value)} placeholder="Search fabrics, colors, codes, collections…"
          className="w-full pl-9 pr-9 py-2.5 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:border-amber-500" data-testid="swatch-search-input" />
        {f.query && <button type="button" onClick={() => f.setQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700" aria-label="Clear search" data-testid="swatch-search-clear"><X className="w-4 h-4" /></button>}
      </label>
      {f.brands.length > 1 && <Select value={f.brand} onChange={f.setBrand} all={f.brands} counts={f.facets.brand} label="All brands" testid="swatch-brand-select" />}
      <Select value={f.collection} onChange={f.setCollection} all={f.collections} counts={f.facets.collection} label="All collections" testid="swatch-collection-select" />
    </div>
    {f.colors.length > 1 && <ColorChips f={f} />}
    {f.types.length > 1 && (
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:flex-wrap sm:overflow-visible" data-testid="swatch-type-chips">
        <Chip active={!f.type} onClick={() => f.setType('')} testid="swatch-type-all">All types</Chip>
        {f.types.map((t) => <Chip key={t} active={f.type === t} onClick={() => f.setType(f.type === t ? '' : t)} disabled={!f.facets.type[t] && f.type !== t} testid={`swatch-type-${slug(t)}`}>{t} <span className="opacity-60">{f.facets.type[t] || 0}</span></Chip>)}
      </div>
    )}
    <ActiveFilters f={f} />
    <p className="text-xs text-neutral-500" data-testid="swatch-count">Showing {f.visible.length} of {f.filtered.length}{f.filtered.length !== total ? ` (${total} total)` : ''} fabrics</p>
  </div>
);

export const LoadMore = ({ f }) => (f.visible.length < f.filtered.length ? (
  <div className="mt-5 text-center">
    <button type="button" onClick={f.loadMore} className="px-5 py-2.5 rounded-full border border-neutral-300 text-sm font-medium text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 transition-colors" data-testid="swatch-load-more">
      Load more ({Math.min(PAGE_SIZE, f.filtered.length - f.visible.length)})
    </button>
  </div>
) : null);
